import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive, Pagination } from "@/pages/Senders";
import { Target, Plus, Trash, Eye } from "@phosphor-icons/react";

/**
 * Inbox Placement — Send the same email from selected senders to your
 * OAuth-connected seed mailboxes, then poll each seed's Gmail labels to
 * determine placement (inbox / spam / promotions / updates / social /
 * forums / trash / other / not_found).
 */
export default function InboxPlacement() {
  const [tests, setTests] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const r = await api.get("/inbox-placement", { params: { limit: pageSize, skip: page * pageSize } });
    setTests(r.data.tests || []);
    setTotal(r.data.total || 0);
  };
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); /* eslint-disable-next-line */ }, [page, pageSize]);

  const remove = async (id, name) => {
    if (!confirm(`Delete inbox-placement test "${name}"? Results also deleted.`)) return;
    try {
      await api.delete(`/inbox-placement/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="inbox-placement-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-mono uppercase tracking-wider flex items-center gap-2">
            <Target size={22} weight="bold" className="text-[#E1FF00]" /> Inbox Placement
          </h1>
          <p className="text-xs text-[#71717A] mt-1 font-mono max-w-2xl">
            Send a probe email from your senders to every connected seed Gmail —
            then see which landed in Inbox vs Spam vs Promotions.
          </p>
        </div>
        <BtnPrimary onClick={() => setShowNew(true)} testid="ip-new-btn">
          <Plus size={13} weight="bold" /> New placement test
        </BtnPrimary>
      </div>

      {showNew && <NewTestModal onClose={() => setShowNew(false)} onCreated={load} />}

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
        <table className="dtable w-full" data-testid="ip-tests-table">
          <thead>
            <tr>
              <th>Name</th><th>Status</th>
              <th>Seeds</th><th>Sent</th><th>Inbox</th><th>Spam</th><th>Promo</th>
              <th>Started</th><th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tests.length === 0 && (
              <tr><td colSpan={9} className="text-center text-[#71717A] py-8 font-mono">
                [ no tests yet — click &quot;New placement test&quot; ]
              </td></tr>
            )}
            {tests.map((t) => {
              const c = t.counts || {};
              return (
                <tr key={t.id}>
                  <td className="font-mono text-xs">
                    <Link to={`/inbox-placement/${t.id}`} className="hover:text-[#E1FF00]">{t.name}</Link>
                    <div className="text-[10px] text-[#71717A]">[IPT-{t.tag_token}] · {(t.subject || "").slice(0, 40)}</div>
                  </td>
                  <td><StatusPill status={t.status} /></td>
                  <td className="font-mono text-xs">{(t.seed_mailbox_ids || []).length}</td>
                  <td className="font-mono text-xs text-[#00E5FF]">{c.sent || 0}</td>
                  <td className="font-mono text-xs text-[#00E5FF]">{c.inbox || 0}</td>
                  <td className="font-mono text-xs text-[#FF3B30]">{c.spam || 0}</td>
                  <td className="font-mono text-xs text-[#E1FF00]">{c.promotions || 0}</td>
                  <td className="font-mono text-[10px] text-[#71717A]">{(t.started_at || t.created_at || "").slice(0, 19)}</td>
                  <td>
                    <div className="flex gap-1.5">
                      <BtnSecondary onClick={() => window.location.assign(`/inbox-placement/${t.id}`)} testid={`ip-open-${t.id}`}>
                        <Eye size={11} weight="bold" /> Open
                      </BtnSecondary>
                      <BtnDestructive onClick={() => remove(t.id, t.name)} testid={`ip-delete-${t.id}`}>
                        <Trash size={11} weight="bold" />
                      </BtnDestructive>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
        sizeOptions={[25, 50, 200]}
        testidPrefix="ip-pg"
      />
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    queued: { color: "#71717A", label: "queued" },
    sending: { color: "#E1FF00", label: "sending" },
    polling: { color: "#00E5FF", label: "polling" },
    cooldown: { color: "#A855F7", label: "cooldown" },
    complete: { color: "#00E5FF", label: "complete" },
    failed: { color: "#FF3B30", label: "failed" },
  };
  const s = map[status] || map.queued;
  return <span className="inline-flex text-[10px] font-mono px-2 py-0.5 border" style={{ color: s.color, borderColor: `${s.color}55` }}>{s.label}</span>;
}

function NewTestModal({ onClose, onCreated }) {
  const [name, setName] = useState(`IPT ${new Date().toISOString().slice(0, 16).replace("T", " ")}`);
  const [subject, setSubject] = useState("Quick question about your team");
  const [body, setBody] = useState("Hi [firstName],\n\nI wanted to reach out briefly — are you the right person to speak with about improving your outbound workflow?\n\nAppreciate a quick reply either way.\n\nThanks,\n[senderFullName]");
  const [contentMode, setContentMode] = useState("manual");
  const [aiInstructions, setAiInstructions] = useState("");
  // Rotation controls — 0 rotation_size means "use N_seeds" on backend
  const [rotationSize, setRotationSize] = useState(0);
  const [rotationDelay, setRotationDelay] = useState(0);
  const [pollConcurrency, setPollConcurrency] = useState(5);
  const [poolReadyCount, setPoolReadyCount] = useState(null);
  // Sender list — first 500 shown; search re-queries backend so senders
  // outside the initial 500 stay reachable.
  const [senders, setSenders] = useState([]);
  const [sendersTotal, setSendersTotal] = useState(0);
  const [selectedSenders, setSelectedSenders] = useState(new Set());
  // Selection cache — { id -> email } across every sender the user has
  // ever ticked, INCLUDING senders no longer in the currently-loaded page.
  // This is what we submit to the backend at Start.
  const [senderCache, setSenderCache] = useState({});
  const [senderSearch, setSenderSearch] = useState("");
  // Same shape for seeds.
  const [seeds, setSeeds] = useState([]);
  const [seedsTotal, setSeedsTotal] = useState(0);
  const [selectedSeeds, setSelectedSeeds] = useState(new Set());
  const [seedCache, setSeedCache] = useState({});
  const [seedSearch, setSeedSearch] = useState("");
  const [creating, setCreating] = useState(false);

  const loadSenders = async (search) => {
    const r = await api.get("/senders", {
      params: { limit: 500, proxy_status: "valid", search: search || undefined },
    });
    setSenders(r.data.senders || []);
    setSendersTotal(r.data.total || 0);
    // Warm the cache with visible rows so id→email lookups don't require
    // re-fetching for display purposes.
    setSenderCache((prev) => {
      const n = { ...prev };
      for (const s of r.data.senders || []) n[s.id] = s.email;
      return n;
    });
  };
  const loadSeeds = async (search) => {
    const r = await api.get("/seed-mailboxes", { params: { limit: 500, search: search || undefined } });
    setSeeds(r.data.mailboxes || []);
    setSeedsTotal(r.data.total || 0);
    setSeedCache((prev) => {
      const n = { ...prev };
      for (const s of r.data.mailboxes || []) n[s.id] = s.email;
      return n;
    });
  };

  useEffect(() => { loadSenders(""); loadSeeds(""); }, []);

  // Debounced backend search (250ms) — the backend already supports `search`
  // param via case-insensitive regex on email/name.
  useEffect(() => {
    const t = setTimeout(() => loadSenders(senderSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [senderSearch]);
  useEffect(() => {
    const t = setTimeout(() => loadSeeds(seedSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [seedSearch]);

  // Show pool inventory when pool mode selected
  useEffect(() => {
    if (contentMode !== "pool") return;
    (async () => {
      try {
        const r = await api.get("/content-pool");
        setPoolReadyCount(r.data?.available ?? 0);
      } catch { setPoolReadyCount(null); }
    })();
  }, [contentMode]);

  const toggle = (setter, current, id, cacheSetter, email) => {
    const n = new Set(current);
    n.has(id) ? n.delete(id) : n.add(id);
    setter(n);
    if (email && cacheSetter) cacheSetter((prev) => ({ ...prev, [id]: email }));
  };
  const toggleAllVisible = (list, current, setter, cacheSetter) => {
    const visibleIds = list.map((s) => s.id);
    const allVisibleOn = visibleIds.length > 0 && visibleIds.every((id) => current.has(id));
    const n = new Set(current);
    if (allVisibleOn) visibleIds.forEach((id) => n.delete(id));
    else visibleIds.forEach((id) => n.add(id));
    setter(n);
    if (cacheSetter) {
      cacheSetter((prev) => {
        const cn = { ...prev };
        for (const s of list) cn[s.id] = s.email;
        return cn;
      });
    }
  };

  // Bulk-paste — resolves against the FULL backend set via /senders/lookup or
  // /seed-mailboxes/lookup, not just the currently-loaded 500.
  const bulkAddBackend = (endpoint, current, setter, cacheSetter) => async (tokens) => {
    try {
      const { data } = await api.post(endpoint, { emails: tokens });
      const matched = data.matched || [];
      const n = new Set(current);
      const cn = {};
      for (const m of matched) { n.add(m.id); cn[m.id] = m.email; }
      setter(n);
      cacheSetter((prev) => ({ ...prev, ...cn }));
      return { matched: matched.length, missed: (data.missed || []).length };
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
      return { matched: 0, missed: tokens.length };
    }
  };

  const create = async () => {
    if (!selectedSenders.size) return toast.error("Select at least one sender");
    if (!selectedSeeds.size) return toast.error("Select at least one seed mailbox");
    if (contentMode === "manual" && (!subject.trim() || !body.trim())) {
      return toast.error("Subject and body are required in manual mode");
    }
    if (contentMode === "pool" && (poolReadyCount || 0) < selectedSenders.size) {
      const ok = confirm(`Pool has only ${poolReadyCount || 0} ready items but you're firing ${selectedSenders.size} probes (one per sender). Some sends will fail. Continue?`);
      if (!ok) return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/inbox-placement", {
        name: name.trim(),
        subject: subject.trim(),
        body,
        content_mode: contentMode,
        ai_instructions: aiInstructions,
        sender_ids: Array.from(selectedSenders),
        seed_mailbox_ids: Array.from(selectedSeeds),
        rotation_size: Number(rotationSize) || 0,
        rotation_delay_seconds: Number(rotationDelay) || 0,
        poll_concurrency: Number(pollConcurrency) || 5,
      });
      toast.success(`Test started — ${selectedSenders.size} probes queued`);
      onCreated?.();
      onClose();
      window.location.assign(`/inbox-placement/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/75 z-40 flex items-center justify-center p-4" onClick={onClose} data-testid="ip-new-modal">
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-mono uppercase tracking-wider">New Inbox Placement Test</h2>
          <button onClick={onClose} className="text-[#71717A] hover:text-white font-mono text-xs">[×]</button>
        </div>

        <label className="micro block mb-1">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="ip-new-name"
          className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none mb-4" />

        <label className="micro block mb-1">Content source</label>
        <div className="flex gap-2 mb-3" data-testid="ip-new-mode">
          {[
            { v: "manual", l: "Manual" },
            { v: "pool", l: "From Pool" },
            { v: "ai_realtime", l: "DeepSeek realtime" },
          ].map((o) => (
            <button key={o.v} onClick={() => setContentMode(o.v)}
              data-testid={`ip-new-mode-${o.v}`}
              className={`px-3 py-2 text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                contentMode === o.v
                  ? "border-[#E1FF00] text-[#E1FF00] bg-[#E1FF00]/5"
                  : "border-[#2A2A2A] text-[#71717A] hover:text-white"
              }`}>
              {o.l}
            </button>
          ))}
        </div>
        {contentMode === "pool" && (
          <div className="text-[11px] font-mono text-[#71717A] mb-3">
            Pool ready: <span className="text-[#00E5FF]">{poolReadyCount ?? "…"}</span> items — one consumed per sender (probe).{" "}
            {poolReadyCount != null && poolReadyCount < selectedSenders.size && (
              <span className="text-[#FF3B30]">
                Not enough items for {selectedSenders.size} probes — generate more on <a href="/content-pool" className="underline">Content Pool</a>.
              </span>
            )}
          </div>
        )}
        {contentMode === "ai_realtime" && (
          <div className="mb-3">
            <label className="micro block mb-1">AI instructions (optional)</label>
            <textarea value={aiInstructions} onChange={(e) => setAiInstructions(e.target.value)} rows={2}
              placeholder="e.g. keep tone casual, mention Q1 outreach"
              data-testid="ip-new-ai-instructions"
              className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
          </div>
        )}

        {contentMode === "manual" && (
          <>
            <label className="micro block mb-1">Subject (we auto-append [IPT-token] to find the message)</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ip-new-subject"
              className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none mb-3" />

            <label className="micro block mb-1">Body (plain text — [firstName], [senderFullName] supported)</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} data-testid="ip-new-body"
              className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none mb-4" />
          </>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <ListPicker
            title="Senders"
            testid="ip-new-senders"
            items={senders}
            totalCount={sendersTotal}
            selectedIds={selectedSenders}
            selectionCache={senderCache}
            onToggleId={(id, email) => toggle(setSelectedSenders, selectedSenders, id, setSenderCache, email)}
            onToggleAll={() => toggleAllVisible(senders, selectedSenders, setSelectedSenders, setSenderCache)}
            onBulkAdd={bulkAddBackend("/senders/lookup", selectedSenders, setSelectedSenders, setSenderCache)}
            search={senderSearch}
            onSearchChange={setSenderSearch}
            searchTestid="ip-new-sender-search"
            emptyLabel="[ no matching senders ]"
            secondaryLabel={(s) => s.proxy_host}
          />
          <ListPicker
            title="Seed mailboxes"
            testid="ip-new-seeds"
            items={seeds}
            totalCount={seedsTotal}
            selectedIds={selectedSeeds}
            selectionCache={seedCache}
            onToggleId={(id, email) => toggle(setSelectedSeeds, selectedSeeds, id, setSeedCache, email)}
            onToggleAll={() => toggleAllVisible(seeds, selectedSeeds, setSelectedSeeds, setSeedCache)}
            onBulkAdd={bulkAddBackend("/seed-mailboxes/lookup", selectedSeeds, setSelectedSeeds, setSeedCache)}
            search={seedSearch}
            onSearchChange={setSeedSearch}
            searchTestid="ip-new-seed-search"
            emptyLabel={<>[ no seeds — connect one in <a className="underline text-[#00E5FF]" href="/seed-mailboxes">Seed Gmail</a> ]</>}
            secondaryLabel={(s) => s.label || s.oauth_project_name}
          />
        </div>

        <div className="text-[10px] font-mono text-[#71717A] mb-2">
          Rotation preview: probes fire in order of sender email (a→z). Each probe uses
          <span className="text-white"> sender[i] → seed[i mod {selectedSeeds.size || "N"}]</span>.
          Total probes = <span className="text-white">{selectedSenders.size || "N_senders"}</span>.
          {(() => {
            const rs = Number(rotationSize) || (selectedSeeds.size || 0);
            const total = selectedSenders.size || 0;
            const nRot = rs > 0 && total > 0 ? Math.ceil(total / rs) : 0;
            return nRot > 0 ? (
              <> Sent in <span className="text-white">{nRot}</span> rotations of <span className="text-white">{Math.min(rs, total)}</span>{rotationDelay > 0 ? <> with <span className="text-white">{rotationDelay}s</span> cooldown</> : null}.</>
            ) : null;
          })()}
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4 border border-[#2A2A2A] bg-[#050505] p-3">
          <div>
            <label className="micro block mb-1">Rotation size</label>
            <input type="number" min={0} value={rotationSize}
              onChange={(e) => setRotationSize(e.target.value)}
              data-testid="ip-new-rotation-size"
              placeholder={`${selectedSeeds.size || "N_seeds"}`}
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-2 py-1.5 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
            <div className="text-[9px] text-[#71717A] mt-1 font-mono">
              probes per rotation (0 = auto: N_seeds)
            </div>
          </div>
          <div>
            <label className="micro block mb-1">Delay between rotations (s)</label>
            <input type="number" min={0} value={rotationDelay}
              onChange={(e) => setRotationDelay(e.target.value)}
              data-testid="ip-new-rotation-delay"
              placeholder="0"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-2 py-1.5 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
            <div className="text-[9px] text-[#71717A] mt-1 font-mono">
              cooldown after each batch
            </div>
          </div>
          <div>
            <label className="micro block mb-1">Poll concurrency</label>
            <input type="number" min={1} max={20} value={pollConcurrency}
              onChange={(e) => setPollConcurrency(e.target.value)}
              data-testid="ip-new-poll-concurrency"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-2 py-1.5 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
            <div className="text-[9px] text-[#71717A] mt-1 font-mono">
              max seeds polled in parallel (1–20)
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <BtnSecondary onClick={onClose} testid="ip-new-cancel">Cancel</BtnSecondary>
          <BtnPrimary onClick={create} disabled={creating} testid="ip-new-create">
            {creating ? "Starting..." : "Start test"}
          </BtnPrimary>
        </div>
      </div>
    </div>
  );
}

function ListPicker({
  title, testid, items, totalCount, selectedIds, selectionCache, onToggleId, onToggleAll,
  onBulkAdd,
  search, onSearchChange, searchTestid, emptyLabel, secondaryLabel,
}) {
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const applyBulk = async () => {
    const tokens = bulkText.split(/[\s,;]+/).map((t) => t.trim().toLowerCase()).filter(Boolean);
    if (!tokens.length) { toast.error("Paste at least one email"); return; }
    setBulkBusy(true);
    try {
      const { matched, missed } = await onBulkAdd(tokens);
      setBulkText("");
      setBulkOpen(false);
      toast.success(`Selected ${matched} · ${missed} not in database`);
    } finally { setBulkBusy(false); }
  };

  const shownCount = items.length;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="micro">{title} ({selectedIds.size} / {totalCount})</div>
        <div className="flex gap-2">
          <button onClick={() => setBulkOpen((o) => !o)}
            data-testid={`${testid}-bulk-toggle`}
            className="text-[10px] text-[#00E5FF] hover:text-white font-mono uppercase">
            bulk paste
          </button>
          <button onClick={onToggleAll}
            data-testid={`${testid}-select-all`}
            className="text-[10px] text-[#E1FF00] hover:text-white font-mono uppercase">
            select {shownCount ? `visible (${shownCount})` : "all"}
          </button>
        </div>
      </div>
      <input type="text" value={search} onChange={(e) => onSearchChange(e.target.value)}
        placeholder="search backend by email or name..." data-testid={searchTestid}
        className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-1.5 text-[11px] text-white font-mono focus:border-[#E1FF00] focus:outline-none mb-1" />
      {bulkOpen && (
        <div className="mb-2 border border-[#00E5FF]/40 bg-[#00E5FF]/5 p-2" data-testid={`${testid}-bulk-panel`}>
          <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={3}
            placeholder="Paste emails — comma / newline / space separated. Resolved against the FULL database, not just visible rows."
            data-testid={`${testid}-bulk-textarea`}
            className="w-full bg-[#050505] border border-[#2A2A2A] px-2 py-1 text-[11px] text-white font-mono focus:border-[#00E5FF] focus:outline-none" />
          <div className="flex justify-end gap-2 mt-1">
            <button onClick={() => { setBulkOpen(false); setBulkText(""); }} disabled={bulkBusy}
              className="text-[10px] text-[#71717A] hover:text-white font-mono uppercase px-2">
              cancel
            </button>
            <button onClick={applyBulk} disabled={bulkBusy} data-testid={`${testid}-bulk-apply`}
              className="text-[10px] text-[#00E5FF] hover:text-white font-mono uppercase border border-[#00E5FF]/40 px-2 py-1 disabled:opacity-40">
              {bulkBusy ? "resolving..." : "apply"}
            </button>
          </div>
        </div>
      )}
      <div className="border border-[#2A2A2A] bg-[#050505] max-h-56 overflow-y-auto" data-testid={testid}>
        {items.length === 0 && <div className="p-4 text-center text-[#71717A] text-xs font-mono">{emptyLabel}</div>}
        {items.map((s) => (
          <label key={s.id} className="flex items-center gap-2 p-2 hover:bg-[#2A2A2A]/30 text-xs font-mono cursor-pointer">
            <input type="checkbox" checked={selectedIds.has(s.id)} onChange={() => onToggleId(s.id, s.email)} />
            <span className="truncate flex-1">{s.email}</span>
            {secondaryLabel?.(s) && <span className="text-[10px] text-[#71717A] shrink-0 max-w-[120px] truncate">{secondaryLabel(s)}</span>}
          </label>
        ))}
      </div>
      {selectedIds.size > 0 && (
        <div className="mt-1 text-[10px] font-mono text-[#71717A]">
          {selectedIds.size} selected across pages
          {selectionCache && Object.keys(selectionCache).length > 0 && (
            <span className="ml-1">
              (incl. rows outside current view)
            </span>
          )}
        </div>
      )}
    </div>
  );
}
